// Shared chat-rendering helpers for the planner-style streaming chat surfaces.
// Both the full Workflow Planner modal and the on-canvas "AI Flow Builder"
// balloon stream from the same backend (/workflow-planner/chat/stream) and so
// share the same lightweight markdown + XML-tag handling. Kept here (instead of
// duplicated in each component) per the repo's "modular / no repetition" rule.
const React = require('react');
const { createElement: h } = React;

// Render a plain-text string with lightweight markdown:
//   - **bold**
//   - `code`
//   - newlines -> <br>
//   - lines starting with "- " -> bullet
function formatText(text) {
  if (!text) return text;
  var lines = text.split('\n');
  var result = [];
  lines.forEach(function(line, li) {
    if (li > 0) result.push(h('br', { key: 'br-' + li }));
    var trimmed = line.trimStart();
    var isBullet = trimmed.startsWith('- ');
    var content = isBullet ? trimmed.slice(2) : line;
    // Split by **bold** and `code`
    var parts = [];
    var pattern = /(\*\*(.+?)\*\*|`([^`]+)`)/g;
    var last = 0;
    var m;
    while ((m = pattern.exec(content)) !== null) {
      if (m.index > last) parts.push(content.slice(last, m.index));
      if (m[2]) {
        parts.push(h('strong', { key: 'b-' + li + '-' + m.index }, m[2]));
      } else if (m[3]) {
        parts.push(h('code', { key: 'c-' + li + '-' + m.index, className: 'planner-inline-code' }, m[3]));
      }
      last = m.index + m[0].length;
    }
    if (last < content.length) parts.push(content.slice(last));
    if (isBullet) {
      result.push(h('span', { key: 'li-' + li, className: 'planner-msg-bullet' }, '• ', parts));
    } else {
      result.push.apply(result, parts);
    }
  });
  return result;
}

// Strip in-progress XML tags from streaming text so users don't see raw markup.
// Removes complete <question>/<option>/<notes>/<probe>/<workflow>/<node> blocks,
// AND any incomplete/open tag at the end (e.g. "<question label=" or "<node agent=").
function stripStreamingXml(text) {
  if (!text) return '';
  // Remove complete blocks
  var cleaned = text
    .replace(/<question\b[^>]*>[\s\S]*?<\/question>/gi, '')
    .replace(/<option\b[^>]*>[\s\S]*?<\/option>/gi, '')
    .replace(/<notes>[\s\S]*?<\/notes>/gi, '')
    .replace(/<probe\s+type="[^"]*"(?:\s+label="[^"]*")?>[\s\S]*?<\/probe>/gi, '')
    .replace(/<workflow>[\s\S]*?<\/workflow>/gi, '')
    .replace(/<plan_preview>[\s\S]*?<\/plan_preview>/gi, '')
    .replace(/<plan>[\s\S]*?<\/plan>/gi, '')
    .replace(/<node\b[^>]*>[\s\S]*?<\/node>/gi, '')
    .replace(/<memory_permission_ask\b[^>]*>[\s\S]*?<\/memory_permission_ask>/gi, '')
    .replace(/<workflow_suggestions>[\s\S]*?<\/workflow_suggestions>/gi, '')
    .replace(/<web_search\b[^>]*\/?>/gi, '');
  // Remove incomplete/open blocks that haven't closed yet (e.g. "<workflow>{...json..." or "<node agent=...")
  cleaned = cleaned
    .replace(/<workflow_suggestions>[\s\S]*$/i, '')
    .replace(/<workflow>[\s\S]*$/i, '')
    .replace(/<plan_preview>[\s\S]*$/i, '')
    .replace(/<plan>[\s\S]*$/i, '')
    .replace(/<node\b[\s\S]*$/i, '')
    .replace(/<notes>[\s\S]*$/i, '')
    .replace(/<probe\s+[^>]*>[\s\S]*$/i, '')
    .replace(/<question\s+[^>]*>[\s\S]*$/i, '')
    .replace(/<memory_permission_ask\b[^>]*$/i, '')
    .replace(/<web_search\b[\s\S]*$/i, '');
  // When the discovery model emits <ready/> alongside a premature inline workflow
  // as a ```json fence or bare {"nodes":...} object, drop it so the user never sees
  // raw JSON. The server's generation pass produces the displayed message.
  if (/<\/?ready\s*\/?>/i.test(cleaned)) {
    cleaned = cleaned
      .replace(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/gi, function(match, body) {
        return /"nodes"\s*:\s*\[|"connections"\s*:\s*\[|"wn"\s*:|"\s*n"\s*:\s*\[|"\s*c"\s*:\s*\[/.test(body) ? '' : match;
      })
      .replace(/```(?:json)?\s*\{[\s\S]*$/i, function(match) {
        return /"nodes"\s*:\s*\[|"wn"\s*:|"\s*n"\s*:\s*\[/.test(match) ? '' : match;
      })
      .replace(/\{\s*"(?:wn|nodes)"\s*:[\s\S]*\}\s*$/i, '');
  }
  // <ready/> is a control token (server kicked off generation), never user-facing
  // text — drop it (complete OR a trailing partial like "<ready" at the stream end).
  cleaned = cleaned
    .replace(/<\/?ready\s*\/?>/gi, '')
    .replace(/<\/?ready\b[^>]*$/i, '');
  // Remove any trailing partial tag start
  cleaned = cleaned.replace(/<\/?[a-z]*[^>]*$/i, '');
  return cleaned.trim();
}

// Parse an AI message to extract <question> blocks with <option> tags, plus
// <plan_preview> and <memory_permission_ask>. Text outside these tags is plain.
// Expected question format from the AI:
//   <question label="What type of database?">
//     <option>PostgreSQL</option>
//     <option>MySQL</option>
//   </question>
// Add multi="true" when several answers may apply — the control then renders
// checkboxes and `selected` becomes an array of labels instead of a string:
//   <question label="Which of these eat your time?" multi="true"> ... </question>
// Returns { parts: [{ type:'text', text } | { type:'question', question, options } | ...] }
function parseAiMessage(content) {
  if (!content || typeof content !== 'string') return { parts: [{ type: 'text', text: content || '' }] };

  // <ready/> is a control token, not display text — strip it before parsing so a
  // turn that's only "<ready/>" renders as empty (and produces no text bubble).
  content = content.replace(/<\/?ready\s*\/?>/gi, '');

  // Combined matcher: walks <question>, <plan_preview>, AND <memory_permission_ask>
  // in order so they render at the right position relative to surrounding text.
  var parts = [];
  var pattern = /<question\s+([^>]*?)>([\s\S]*?)<\/question>|<plan_preview>([\s\S]*?)<\/plan_preview>|<memory_permission_ask\b([^>]*)>([\s\S]*?)<\/memory_permission_ask>/gi;
  var lastIndex = 0;
  var match;

  while ((match = pattern.exec(content)) !== null) {
    var before = content.slice(lastIndex, match.index).trim();
    if (before) parts.push({ type: 'text', text: before });

    if (match[1] !== undefined) {
      // <question> match. Options are { label, desc } objects — desc comes from
      // the optional desc="..." attribute (<option desc="why pick this">Label</option>).
      // multi="true" marks a question where several answers may apply.
      var qAttrs = match[1] || '';
      var labelAttr = qAttrs.match(/label\s*=\s*"([^"]*)"/i);
      var questionLabel = labelAttr ? labelAttr[1].trim() : '';
      // Optional topic="..." — a 2-4 word subject used as the tab label when
      // several questions render as tabs; the full question is too long there.
      var topicAttr = qAttrs.match(/topic\s*=\s*"([^"]*)"/i);
      var questionTopic = topicAttr ? topicAttr[1].trim() : '';
      var isMulti = /\bmulti\s*=\s*"\s*(?:true|yes|1)\s*"/i.test(qAttrs);
      var optionsBlock = match[2];
      var options = [];
      var optPattern = /<option\b([^>]*)>([\s\S]*?)<\/option>/gi;
      var optMatch;
      while ((optMatch = optPattern.exec(optionsBlock)) !== null) {
        var optText = optMatch[2].trim();
        if (!optText) continue;
        var descMatch = (optMatch[1] || '').match(/desc\s*=\s*"([^"]*)"/i);
        options.push({ label: optText, desc: descMatch ? descMatch[1].trim() : '' });
      }
      if (options.length > 0) {
        parts.push({ type: 'question', question: questionLabel, topic: questionTopic, options: options, multi: isMulti });
      } else {
        parts.push({ type: 'input', question: questionLabel, topic: questionTopic });
      }
    } else if (match[4] !== undefined) {
      // <memory_permission_ask topics="..."> match -- render as Yes/No.
      var topicsAttr = match[4] || '';
      var topicsMatch = topicsAttr.match(/topics\s*=\s*"([^"]*)"/i);
      var topics = topicsMatch ? topicsMatch[1].trim() : '';
      var inner = (match[5] || '').trim();
      var promptText = inner || (topics
        ? 'I have stored notes about ' + topics + ' from prior workflows. May I use them to help plan this one?'
        : 'I have relevant stored notes from prior workflows. May I use them to help plan this one?');
      // bareReply: when set, submitSelections sends only the answer (e.g. "Yes")
      // instead of "<question>: Yes". Required so the server's classifyMemoryConsentReply
      // sees a leading-yes/no token and registers grant/deny.
      parts.push({ type: 'question', question: promptText, options: [{ label: 'Yes', desc: '' }, { label: 'No', desc: '' }], bareReply: true });
    } else {
      // <plan_preview> match
      var ppInner = match[3] || '';
      var summaryMatch = ppInner.match(/<summary>([\s\S]*?)<\/summary>/i);
      var positiveMatch = ppInner.match(/<positive>([\s\S]*?)<\/positive>/i);
      var negativeMatch = ppInner.match(/<negative>([\s\S]*?)<\/negative>/i);
      var summary = summaryMatch ? summaryMatch[1].trim() : '';
      var summarySteps = summary
        ? summary.split('\n').map(function(s) {
            return s.replace(/^\s*[-*]\s*/, '').trim();
          }).filter(function(s) { return s; })
        : [];
      parts.push({
        type: 'plan_preview',
        summary: summarySteps,
        positive: positiveMatch ? positiveMatch[1].trim() : '',
        negative: negativeMatch ? negativeMatch[1].trim() : ''
      });
    }

    lastIndex = match.index + match[0].length;
  }

  var remaining = content.slice(lastIndex).trim();
  if (remaining) parts.push({ type: 'text', text: remaining });

  if (parts.length === 0) parts.push({ type: 'text', text: content });

  return { parts: parts };
}

// Decide whether a FINISHED planner turn stopped prematurely — i.e. it neither
// asked the user anything nor reached a terminal signal, so it left the user
// staring at an outline with nothing to click. When true, the caller auto-sends
// "continue" so discovery keeps going instead of stalling.
//
// A turn is NOT premature (we must wait for the user / it's already done) when
// the message carries any of:
//   - a <question> / free-text <input>  (it's asking — never auto-continue)
//   - a <memory_permission_ask>         (Yes/No consent — same as a question)
//   - a <plan_preview>                  (user must click "Generate now")
//   - a <ready/> signal                 (server is generating the workflow)
// The caller also separately gates on evt.complete (a plan was produced) and on
// any pending Connect/retry, so this helper only judges the message text.
function isPrematureStop(rawMessage) {
  if (!rawMessage || typeof rawMessage !== 'string') return false;
  // <ready/> means the server kicked off generation — not a stall.
  if (/<\/?ready\s*\/?>/i.test(rawMessage)) return false;
  var parsed = parseAiMessage(rawMessage);
  var awaitsUser = parsed.parts.some(function(p) {
    return p.type === 'question' || p.type === 'input' || p.type === 'plan_preview';
  });
  if (awaitsUser) return false;
  // Nothing to act on and no terminal signal — only plain text remains.
  return true;
}

// True when any part AFTER index `idx` is a question/input block. The chat
// surfaces use this to keep a message's questions in ONE tabbed control even
// when the model interleaves connector prose ("And one more:") between its
// <question> blocks — that text still renders, but it must not split the tab
// group into stacked single-question blocks (which lose the tab bar and force
// a long scroll).
function hasQuestionPartAfter(parts, idx) {
  for (var i = idx + 1; i < parts.length; i++) {
    var t = parts[i] && parts[i].type;
    if (t === 'question' || t === 'input') return true;
  }
  return false;
}

// Options are { label, desc } objects since desc="" support; older callers and
// tests may still hold plain strings — read them through these two helpers.
function optionLabel(opt) {
  return typeof opt === 'string' ? opt : String((opt && opt.label) || '');
}

function optionDesc(opt) {
  return (opt && typeof opt === 'object') ? String(opt.desc || '') : '';
}

// A multi="true" question holds an ARRAY of chosen labels; every other question
// holds a plain string. Both travel through the same `selected` prop, so read
// them through these two helpers instead of branching at each call site.
function asSelectionList(selected) {
  var list = Array.isArray(selected) ? selected : [selected];
  return list
    .map(function(s) { return s == null ? '' : String(s).trim(); })
    .filter(function(s) { return s; });
}

// The user-visible answer for one question — what gets sent back to the model.
function selectionText(selected) {
  return asSelectionList(selected).join(', ');
}

// Shared interactive Q&A control (one <question> or free-text <input> block):
// card-style option rows (bold label + muted description + number badge), an
// "Other" row with an inline free-text field, and a static fallback for
// non-interactive (already answered) messages. Used by the planner modal and
// the AI Flow balloon so both surfaces look identical.
//
// Props: part, interactive, selected, onSelect(value|''), otherOpen, otherText,
//        onOtherToggle(open, seedText), onOtherText(text)
function QuestionBlock(props) {
  var part = props.part;
  var interactive = props.interactive;
  var selected = props.selected || '';

  if (part.type === 'input') {
    return h('div', { className: 'planner-msg-question-block' },
      h('div', { className: 'planner-msg-question-label' }, part.question),
      interactive
        ? h('textarea', {
            className: 'planner-msg-input-textarea',
            placeholder: 'Type your answer...',
            value: selected,
            rows: 2,
            onChange: function(e) { props.onSelect(e.target.value); }
          })
        : (selected ? h('div', { className: 'planner-msg-input-static' }, selected) : null)
    );
  }

  var options = part.options || [];
  var labels = options.map(optionLabel);
  // multi="true" → tick as many as apply (checkbox rows, no auto-advance);
  // otherwise one answer wins and picking it replaces the previous one.
  var multi = !!part.multi;
  var chosen = asSelectionList(selected);

  if (!interactive) {
    return h('div', { className: 'planner-msg-question-block' },
      h('div', { className: 'planner-msg-question-label' }, part.question),
      h('div', { className: 'planner-msg-options-static' },
        options.map(function(opt, oi) {
          return h('div', { key: oi, className: 'planner-msg-option-static' }, '• ' + optionLabel(opt));
        })
      )
    );
  }

  // Selections that aren't option labels came from the Other field. Multi keeps
  // at most one custom entry alongside any number of ticked options.
  var customPicks = chosen.filter(function(l) { return labels.indexOf(l) === -1; });
  var isCustom = customPicks.length > 0;

  // Right-hand badge: a tickable box on multi questions, the keyboard-style
  // number on single ones. Same slot either way, so the card layout is shared.
  function optionBadge(oi, isSel) {
    if (!multi) return h('span', { className: 'planner-msg-option-num' }, String(oi + 1));
    return h('span', { className: 'planner-msg-option-check' + (isSel ? ' planner-msg-option-check-on' : '') },
      isSel
        ? h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 3 },
            h('polyline', { points: '20 6 9 17 4 12' })
          )
        : null
    );
  }

  function pick(label) {
    props.onOtherToggle(false, '');
    if (!multi) {
      props.onSelect(chosen[0] === label ? '' : label);
      return;
    }
    props.onSelect(chosen.indexOf(label) === -1
      ? chosen.concat([label])
      : chosen.filter(function(l) { return l !== label; }));
  }

  // Commit the Other field. On a multi question the typed answer joins the
  // ticked options (replacing any earlier custom entry); on a single one it
  // replaces the whole answer.
  function commitOther(text) {
    var typed = String(text || '').trim();
    if (!typed) return;
    props.onSelect(multi
      ? chosen.filter(function(l) { return labels.indexOf(l) !== -1; }).concat([typed])
      : typed);
    props.onOtherToggle(false, '');
  }

  var rows = options.map(function(opt, oi) {
    var label = optionLabel(opt);
    var desc = optionDesc(opt);
    var isSel = chosen.indexOf(label) !== -1;
    return h('button', {
      key: oi,
      className: 'planner-msg-option-btn planner-msg-option-card' + (isSel ? ' planner-msg-option-selected' : ''),
      onClick: function() { pick(label); }
    },
      h('div', { className: 'planner-msg-option-main' },
        h('div', { className: 'planner-msg-option-label' }, label),
        desc ? h('div', { className: 'planner-msg-option-desc' }, desc) : null
      ),
      optionBadge(oi, isSel)
    );
  });

  // "Other" free-text row — hidden for bare Yes/No consent asks, where an
  // arbitrary answer would break the server's yes/no reply classifier.
  if (!part.bareReply) {
    var otherBadge = optionBadge(options.length, isCustom);
    if (props.otherOpen) {
      rows.push(h('div', {
        key: 'other',
        className: 'planner-msg-option-btn planner-msg-option-card planner-msg-option-other planner-msg-option-open'
      },
        h('div', { className: 'planner-msg-option-main' },
          h('div', { className: 'planner-msg-option-label' }, 'Other'),
          h('div', { className: 'planner-msg-other-row' },
            h('input', {
              type: 'text',
              className: 'planner-msg-other-field',
              placeholder: 'Type your own answer here',
              value: props.otherText,
              autoFocus: true,
              onChange: function(e) { props.onOtherText(e.target.value); },
              onKeyDown: function(e) {
                if (e.key === 'Enter' && props.otherText.trim()) {
                  e.preventDefault();
                  commitOther(props.otherText);
                } else if (e.key === 'Escape') {
                  props.onOtherToggle(false, '');
                }
              }
            }),
            h('button', {
              className: 'planner-msg-other-submit',
              disabled: !props.otherText.trim(),
              onClick: function() { commitOther(props.otherText); }
            }, 'OK')
          )
        ),
        otherBadge
      ));
    } else {
      rows.push(h('button', {
        key: 'other',
        className: 'planner-msg-option-btn planner-msg-option-card planner-msg-option-other' + (isCustom ? ' planner-msg-option-selected' : ''),
        onClick: function() { props.onOtherToggle(true, customPicks[0] || ''); }
      },
        h('div', { className: 'planner-msg-option-main' },
          h('div', { className: 'planner-msg-option-label' }, 'Other'),
          h('div', { className: 'planner-msg-option-desc' }, isCustom ? customPicks.join(', ') : 'Type your own answer')
        ),
        otherBadge
      ));
    }
  }

  return h('div', { className: 'planner-msg-question-block' },
    h('div', { className: 'planner-msg-question-label' }, part.question),
    multi ? h('div', { className: 'planner-msg-question-hint' }, 'Select all that apply') : null,
    h('div', { className: 'planner-msg-options' }, rows)
  );
}

// Tabbed wrapper for a message carrying MULTIPLE questions: instead of a long
// scroll of stacked question blocks, a tab bar (one pill per question, ✓ once
// answered) shows one QuestionBlock at a time. A single-question run renders
// the block directly with no tab bar. Picking an option auto-advances to the
// next unanswered tab (free-text questions don't auto-advance — onSelect fires
// per keystroke there).
// Props: items  — [{ key, props }] where props are the full QuestionBlock props.
//        footer — optional QuestionSubmitRow props ({ answered, total, onSkip,
//                 onSubmit }). When set, QuestionTabs renders the footer itself
//                 so it can show "Next" on non-final tabs and "Submit" only on
//                 the last one.
function QuestionTabs(props) {
  var items = props.items || [];
  var state = React.useState(0);
  var setActive = state[1];

  if (items.length === 0) return null;
  if (items.length === 1) {
    return h(React.Fragment, null,
      h(QuestionBlock, Object.assign({ key: items[0].key }, items[0].props)),
      props.footer ? h(QuestionSubmitRow, props.footer) : null
    );
  }

  var active = Math.min(state[0], items.length - 1);
  var isLastTab = active === items.length - 1;

  function answered(it) {
    return !!(it.props.selected && String(it.props.selected).trim());
  }

  // Jump to the next unanswered question after `idx` (wrapping). When every
  // question is answered, land on the LAST tab so Submit is in view.
  function advanceFrom(idx) {
    for (var off = 1; off < items.length; off++) {
      var n = (idx + off) % items.length;
      if (!answered(items[n])) { setActive(n); return; }
    }
    setActive(items.length - 1);
  }

  var activeItem = items[active];
  var activeProps = activeItem.props;
  // Multi-select questions never auto-advance — the first tick would jump the
  // user off the question before they finished choosing. They use Next/Submit.
  if (activeProps.interactive && activeProps.part.type === 'question' && !activeProps.part.multi) {
    activeProps = Object.assign({}, activeProps, {
      onSelect: function(value) {
        activeItem.props.onSelect(value);
        if (value && String(value).trim()) advanceFrom(active);
      }
    });
  }

  var footer = null;
  if (props.footer) {
    footer = h(QuestionSubmitRow, isLastTab
      ? props.footer
      : Object.assign({}, props.footer, { onNext: function() { setActive(active + 1); } })
    );
  }

  return h('div', { className: 'planner-msg-qtabs' },
    h('div', { className: 'planner-msg-qtabs-bar' },
      items.map(function(it, i) {
        var done = answered(it);
        // Tab label: the model's short topic="..." when present; the full
        // question (CSS-truncated) only as a fallback for older messages.
        return h('button', {
          key: it.key,
          className: 'planner-msg-qtab'
            + (i === active ? ' planner-msg-qtab-active' : '')
            + (done ? ' planner-msg-qtab-answered' : ''),
          title: it.props.part.question,
          onClick: function() { setActive(i); }
        },
          h('span', { className: 'planner-msg-qtab-num' }, done ? '✓' : String(i + 1)),
          h('span', { className: 'planner-msg-qtab-label' }, it.props.part.topic || it.props.part.question)
        );
      })
    ),
    h(QuestionBlock, Object.assign({ key: activeItem.key }, activeProps)),
    footer
  );
}

// Skip / Submit footer shared by the planner chat surfaces. Submit stays
// visible but disabled until at least one question is answered; Skip lets the
// user hand the decision back to the assistant. With `onNext` set (tabbed
// multi-question control, non-final tab) the primary button becomes "Next"
// and just advances the tab — Submit appears only on the last tab.
// Props: answered, total, onSkip, onSubmit, onNext?
function QuestionSubmitRow(props) {
  return h('div', { className: 'planner-msg-submit-row' },
    h('span', { className: 'planner-msg-submit-count' },
      props.total > 1 ? (props.answered + ' of ' + props.total + ' answered') : ''
    ),
    h('div', { className: 'planner-msg-submit-actions' },
      h('button', { className: 'planner-msg-skip-btn', onClick: props.onSkip }, 'Skip'),
      props.onNext
        ? h('button', { className: 'planner-msg-submit-btn', onClick: props.onNext },
            'Next',
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
              h('polyline', { points: '9 18 15 12 9 6' })
            )
          )
        : h('button', {
            className: 'planner-msg-submit-btn',
            disabled: props.answered === 0,
            onClick: props.onSubmit
          },
            'Submit',
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
              h('line', { x1: 22, y1: 2, x2: 11, y2: 13 }),
              h('polygon', { points: '22 2 15 22 11 13 2 9 22 2' })
            )
          )
    )
  );
}

module.exports = {
  formatText: formatText,
  stripStreamingXml: stripStreamingXml,
  parseAiMessage: parseAiMessage,
  isPrematureStop: isPrematureStop,
  hasQuestionPartAfter: hasQuestionPartAfter,
  optionLabel: optionLabel,
  optionDesc: optionDesc,
  selectionList: asSelectionList,
  selectionText: selectionText,
  QuestionBlock: QuestionBlock,
  QuestionTabs: QuestionTabs,
  QuestionSubmitRow: QuestionSubmitRow
};
